import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { currentCompanyDateFormatSelector } from '@/domain/companies';
import { SYMBOL_FOR_EMPTY_CELL } from '@/components/AgGrid/helpers';

// Right-aligned; editable cells get a pointer cursor so it reads as clickable, matching the
// single-click inline edit below. Editability is read from AG Grid so it tracks the colDef.
const editableCellStyle = ({ column, node }) => ({
  textAlign: 'right',
  cursor: column?.isCellEditable(node) ? 'pointer' : undefined,
});

const useAgGridColumnTypes = () => {
  const dateFormat = useSelector(currentCompanyDateFormatSelector);

  return useMemo(
    () => ({
      string: {
        valueFormatter: ({ value }) => (value != null && value !== '' ? value : SYMBOL_FOR_EMPTY_CELL),
      },
      // Vendor-level withholding tax — numeric percentage (0–100), editable inline on a single click.
      percent: {
        cellEditor: 'withholdingTaxCellEditor',
        cellRenderer: 'vendorFieldCellRenderer',
        singleClickEdit: true,
        cellStyle: editableCellStyle,
        valueFormatter: ({ value }) => (value != null && value !== '' ? `${value}%` : SYMBOL_FOR_EMPTY_CELL),
      },
      // Vendor-level "valid until" — date (not earlier than today), editable inline on a single click.
      date: {
        cellEditor: 'validUntilDateCellEditor',
        cellRenderer: 'vendorFieldCellRenderer',
        singleClickEdit: true,
        cellStyle: editableCellStyle,
        valueFormatter: ({ value }) =>
          value ? moment(value).format(dateFormat) : SYMBOL_FOR_EMPTY_CELL,
      },
    }),
    [dateFormat],
  );
};

export default useAgGridColumnTypes;
